import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from '@/types';
import { getAccessToken, setAccessTokenMem, clearAccessTokenMem } from './tokenStore';

// Derive API prefix from Vite's base path
const basePath = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

// Prepend base path to API routes
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (basePath && config.url?.startsWith('/api/')) {
    config.url = basePath + config.url;
  }
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Token refresh on 401
let isRefreshing = false;
let refreshQueue: Array<{ resolve: (t: string) => void; reject: (e: unknown) => void }> = [];

// v5.8.0 — BroadcastChannel для координации refresh между несколькими tabs
// одного origin'а. Сценарий до: каждый tab независимо детектил 401 и пытался
// /auth/refresh параллельно → backend ротировал refresh-token первого, второй
// приходил со старым → "Token reuse detected" → revoke-all-sessions → ВСЕ tabs
// admin'а вылетают на /auth/login.
//
// Теперь: первый tab делает refresh, broadcast'ит результат (новый access-token)
// другим tabs. Остальные tabs ждут broadcast (с timeout fallback на собственный
// refresh, если leader умер). Только один HTTP-запрос /auth/refresh на cluster
// of tabs.
const REFRESH_CHANNEL = 'swipa-auth-refresh';
const REFRESH_WAIT_MS = 5_000; // ждём leader-broadcast не дольше 5s, потом сами

interface RefreshBroadcast {
  type: 'refresh:success' | 'refresh:fail';
  accessToken?: string;
  ts: number;
}

let bc: BroadcastChannel | null = null;
const pendingBroadcastWaiters: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}> = [];

function getBroadcastChannel(): BroadcastChannel | null {
  if (bc) return bc;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    bc = new BroadcastChannel(REFRESH_CHANNEL);
    bc.onmessage = (ev: MessageEvent<RefreshBroadcast>) => {
      const data = ev.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'refresh:success' && data.accessToken) {
        setAccessTokenMem(data.accessToken);
        // socket reconnect с новым токеном (lazy import — не падать если ESM не доступен).
        import('@/lib/socket').then(({ getSocket }) => {
          try {
            const sock = getSocket();
            sock.auth = { token: data.accessToken };
            if (sock.connected) sock.disconnect();
            sock.connect();
          } catch { /* noop */ }
        }).catch(() => { /* noop */ });
        // Resolve any pending waiters in THIS tab
        while (pendingBroadcastWaiters.length) {
          const w = pendingBroadcastWaiters.shift()!;
          clearTimeout(w.timer);
          w.resolve(data.accessToken);
        }
      } else if (data.type === 'refresh:fail') {
        while (pendingBroadcastWaiters.length) {
          const w = pendingBroadcastWaiters.shift()!;
          clearTimeout(w.timer);
          w.reject(new Error('peer refresh failed'));
        }
      }
    };
  } catch {
    bc = null;
  }
  return bc;
}

/** Wait for another tab's refresh broadcast. Resolves to new token or rejects on timeout. */
function waitForPeerRefresh(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => {
      const idx = pendingBroadcastWaiters.findIndex((w) => w.timer === timer);
      if (idx !== -1) pendingBroadcastWaiters.splice(idx, 1);
      reject(new Error('peer refresh timeout'));
    }, REFRESH_WAIT_MS);
    pendingBroadcastWaiters.push({ resolve, reject, timer });
  });
}

function broadcastRefreshSuccess(accessToken: string) {
  const ch = getBroadcastChannel();
  if (!ch) return;
  try { ch.postMessage({ type: 'refresh:success', accessToken, ts: Date.now() } as RefreshBroadcast); } catch { /* noop */ }
}
function broadcastRefreshFail() {
  const ch = getBroadcastChannel();
  if (!ch) return;
  try { ch.postMessage({ type: 'refresh:fail', ts: Date.now() } as RefreshBroadcast); } catch { /* noop */ }
}

// Eagerly init listener on module load — пассивный listener позволит даже
// "пассивным" tabs получать broadcast от активного refresh-лидера.
getBroadcastChannel();

function isOnLoginRoute(): boolean {
  const p = window.location.pathname;
  return (
    p.endsWith('/auth/login') ||
    p.endsWith('/auth/register') ||
    p.endsWith('/auth/forgot-password') ||
    p.includes('/auth/reset-password') ||
    p.includes('/auth/confirm')
  );
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _adminRetry?: boolean };

    // Never auto-refresh / redirect for auth endpoints themselves.
    // /auth/login, /auth/register, /auth/refresh, /auth/forgot-password etc.
    // are expected to return 401 on wrong credentials or missing token —
    // the caller handles those errors directly. If we let the interceptor
    // hijack them, a bad-password 401 on /login would trigger a
    // /refresh → 401 → redirect-to-login → reload loop.
    const isAuthRoute = !!original.url?.match(/\/api\/auth\//);

    // 403 on admin routes may mean the access token carries a stale role (role
    // was just granted in the DB but the JWT was signed before). Try a token
    // refresh once to pick up the new role; if still 403 after that, the user
    // genuinely lacks permission and the error propagates normally.
    if (
      error.response?.status === 403 &&
      !original._adminRetry &&
      original.url?.includes('/api/admin/')
    ) {
      original._adminRetry = true;
      try {
        const { data } = await apiClient.post<{ accessToken: string }>('/api/auth/refresh');
        setAccessTokenMem(data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return apiClient(original);
      } catch {
        // Refresh failed — propagate original 403
      }
    }

    if (
      error.response?.status === 401 &&
      !original._retry &&
      !isAuthRoute
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token) => {
              original.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(original));
            },
            reject,
          });
        });
      }

      original._retry = true;
      isRefreshing = true;

      // v5.8.0 — пробуем сначала дождаться peer broadcast (если другой tab
      // уже refresh'ит). Это избавляет от parallel /auth/refresh из 2+ tabs,
      // которые triggered "Token reuse detected" на backend.
      let usedPeerToken: string | null = null;
      try {
        usedPeerToken = await waitForPeerRefresh();
      } catch {
        // Никто другой не refresh'ит — делаем сами.
        usedPeerToken = null;
      }

      if (usedPeerToken) {
        try {
          original.headers.Authorization = `Bearer ${usedPeerToken}`;
          refreshQueue.forEach((q) => q.resolve(usedPeerToken!));
          refreshQueue = [];
          return apiClient(original);
        } finally {
          isRefreshing = false;
        }
      }

      try {
        const { data } = await apiClient.post<{ accessToken: string }>('/api/auth/refresh');
        const newToken = data.accessToken;
        setAccessTokenMem(newToken);
        // v5.8.0 — broadcast другим tabs чтобы они не делали свой refresh.
        broadcastRefreshSuccess(newToken);
        // v5.7.6 — реконнект socket с новым токеном.
        // До v5.7.6 axios refresh обновлял REST-token, но socket оставался
        // со старым `s.auth.token` → backend disconnect'ил его за expired
        // token, socket.io reconnect'ился со старым же → silent fail loop.
        // Симптом у юзера: REST API отвечает, но `socket.emit('message:send')`
        // молча буферизуется, сообщения "исчезают" из чата.
        // Per /codex msg 15fr37: обновляем `sock.auth` ВСЕГДА (не только
        // если connected), иначе reconnect после disconnect стартует со
        // старым токеном.
        try {
          const { getSocket } = await import('@/lib/socket');
          const sock = getSocket();
          sock.auth = { token: newToken };
          if (sock.connected) sock.disconnect();
          sock.connect();
        } catch {
          // Lazy import / socket init failed — REST refresh всё равно успешен.
        }
        refreshQueue.forEach((q) => q.resolve(newToken));
        refreshQueue = [];
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      } catch (refreshErr) {
        broadcastRefreshFail();
        refreshQueue.forEach((q) => q.reject(refreshErr));
        refreshQueue = [];
        clearAccessTokenMem();
        // Only navigate away if we're not already on an auth page.
        // Otherwise we'd trigger an infinite reload loop that blocks
        // the user (and DevTools) from inspecting anything.
        if (!isOnLoginRoute()) {
          window.location.href = (import.meta.env.BASE_URL ?? '/') + 'auth/login';
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export function extractError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    return data?.message ?? error.message;
  }
  return String(error);
}

/**
 * Phase 12.1 — same as extractError() but keeps the structured `code` field
 * so callers can switch on it (TOTP_REQUIRED / WORKSPACE_ARCHIVED / …).
 * Use this in TOTP retry flow and workspace error screens.
 */
export interface ExtractedApiError {
  status: number;
  code: string | null;
  message: string;
  details?: unknown;
}
export function extractApiError(error: unknown): ExtractedApiError {
  if (axios.isAxiosError(error)) {
    const data = (error.response?.data ?? {}) as Partial<ApiError> & { details?: unknown };
    return {
      status: error.response?.status ?? 0,
      code: data.code ?? null,
      message: data.message ?? error.message,
      details: data.details,
    };
  }
  return { status: 0, code: null, message: String(error) };
}
